'use client';

import { useState, useCallback, useMemo } from 'react';
import type {
  MapCenter,
  ZipValidationStatus,
  ZipSearchResult,
  ServiceLocationsInteractiveProps,
} from './types';
import {
  DEFAULT_MAP_CENTER,
  DEFAULT_MAP_ZOOM,
  STATE_MAP_CENTERS,
  STATE_MAP_ZOOM,
  ZIP_RESULT_MAP_ZOOM,
} from './constants';
import { getLocationsBrandConfig } from '@/lib/utils/locationsBrandConfig';
import ServiceLocationsSearchForm, { LocalityItem } from './ServiceLocationsSearchForm';
import ServiceLocationsMap from './ServiceLocationsMap';
import DeferUntilVisible from '@/components/DeferUntilVisible/DeferUntilVisible';

export default function ServiceLocationsInteractive({
  apiKey,
  heading,
  subheading,
  stateFilter,
  initialStatus = 'idle',
  initialSearchResult,
  initialZip,
  initialStateLocations = [],
}: ServiceLocationsInteractiveProps) {
  const brandCode = process.env.NEXT_PUBLIC_BRAND ?? '';
  const locationsBrandConfig = getLocationsBrandConfig({ brandCode });

  const brandServiceMapView = locationsBrandConfig.serviceLocationsMapView;

  const brandDefaultMapCenter: MapCenter = brandServiceMapView
    ? {
        lng: brandServiceMapView.center[0],
        lat: brandServiceMapView.center[1],
      }
    : DEFAULT_MAP_CENTER;

  const normalizedStateFilter = stateFilter?.toUpperCase();
  const stateMapCenter = normalizedStateFilter
    ? STATE_MAP_CENTERS[normalizedStateFilter]
    : undefined;

  const initialMapCenter = stateMapCenter ?? brandDefaultMapCenter;
  const initialMapZoom = stateMapCenter
    ? STATE_MAP_ZOOM
    : (brandServiceMapView?.zoom ?? DEFAULT_MAP_ZOOM);

  const [mapCenter, setMapCenter] = useState<MapCenter>(
    initialSearchResult?.lat && initialSearchResult?.lng
      ? { lat: initialSearchResult.lat, lng: initialSearchResult.lng }
      : initialMapCenter,
  );

  const [mapZoom, setMapZoom] = useState(
    initialSearchResult ? ZIP_RESULT_MAP_ZOOM : initialMapZoom,
  );

  const [markerLink, setMarkerLink] = useState<string | null>(initialSearchResult?.link ?? null);
  const [showMarker, setShowMarker] = useState<boolean>(initialStatus === 'valid');

  const [isInitialState, setIsInitialState] = useState<boolean>(
    !!stateFilter && initialStatus === 'idle' && initialStateLocations.length > 0,
  );

  const validPins = useMemo(() => {
    if (!isInitialState || initialStateLocations.length === 0) {
      return [];
    }

    const pins = initialStateLocations.filter((loc) => {
      const matchesFilter = stateFilter
        ? loc.stateShort?.toLowerCase() === stateFilter.toLowerCase() ||
          loc.state?.toLowerCase() === stateFilter.toLowerCase()
        : true;
      return matchesFilter && loc.lat !== null && loc.lng !== null;
    });

    return pins.sort((a, b) => a.locality.localeCompare(b.locality));
  }, [isInitialState, initialStateLocations, stateFilter]);

  const handleSearch = useCallback(
    (_center: MapCenter, status: ZipValidationStatus, result?: ZipSearchResult) => {
      if (status === 'idle') {
        const canBeInitial = !!stateFilter && initialStateLocations.length > 0;
        setIsInitialState(canBeInitial);
        setMapCenter(initialMapCenter);
        setMapZoom(initialMapZoom);
        setMarkerLink(null);
        setShowMarker(initialStatus === 'valid');
        return;
      }

      if (
        status === 'valid' &&
        result?.inServiceArea &&
        result.lat !== null &&
        result.lng !== null
      ) {
        setMapCenter({ lat: result.lat, lng: result.lng });
        setMapZoom(ZIP_RESULT_MAP_ZOOM);
        setMarkerLink(result.link ?? result.results?.[0]?.link ?? null);
        setShowMarker(true);
      } else {
        setShowMarker(false);
      }
      setIsInitialState(false);
    },
    [stateFilter, initialStateLocations.length, initialMapCenter, initialMapZoom, initialStatus],
  );

  const uniqueLocalities = useMemo(() => {
    const seen = new Set<string>();
    return validPins.filter((loc) => {
      const key = `${loc.locality.toLowerCase().trim()}|${loc.link}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [validPins]);

  return (
    <div className='flex max-lg:flex-col lg:gap-x-[128px] items-center'>
      <div className='flex flex-col w-full  shadow-none'>
        {heading && (
          <h2 className='text-primary-500 text-[28px] lg:text-[40px] font-bold text-balance leading-[108%] mb-4 lg:mb-5'>
            {heading}
          </h2>
        )}
        {subheading && (
          <p className='text-primary-500 text-balance text-[16px] lg:mb-[47px]'>{subheading}</p>
        )}

        <div className='w-full  lg:row-start-2 lg:col-start-1 mt-4 lg:mt-0 max-lg:mt-8 max-lg:mb-6'>
          <ServiceLocationsSearchForm
            onSearch={handleSearch}
            stateFilter={stateFilter}
            initialStatus={initialStatus}
            initialSearchResult={initialSearchResult}
            initialZip={initialZip}
          />

          {isInitialState && uniqueLocalities.length > 0 && (
            <div className='mt-6 pt-6 overflow-y-auto max-h-[260px] pr-2'>
              <div className='flex flex-col gap-5'>
                {uniqueLocalities.map((loc, i) => (
                  <LocalityItem
                    key={i}
                    locality={loc.locality}
                    link={loc.link}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='w-full h-[300px] lg:h-[480px] rounded-2xl overflow-hidden bg-zinc-200 lg:row-start-1 lg:row-span-2 lg:col-start-2 mt-8 lg:mt-0 '>
        <DeferUntilVisible className='w-full h-full'>
          <ServiceLocationsMap
            apiKey={apiKey}
            center={mapCenter}
            zoom={mapZoom}
            markerLink={markerLink}
            showMarker={isInitialState || showMarker}
            locations={isInitialState ? validPins : []}
          />
        </DeferUntilVisible>
      </div>
    </div>
  );
}
